<template>
	<div v-if="hasAssessmentRules" class="rounded-md border border-blue-100 bg-blue-50/60 p-3 space-y-3">
		<div class="text-xs font-semibold uppercase tracking-wide text-blue-700">Assessment Rules</div>
		<div v-if="scoringEntries.length" class="space-y-1">
			<div class="text-[11px] font-semibold uppercase tracking-wide text-blue-700">{{ scoringSectionTitle }}</div>
			<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
				<div
					v-for="item in scoringEntries"
					:key="item.key"
					class="rounded bg-white/90 border border-blue-100 p-2 h-full flex items-center"
				>
					<div class="w-full flex items-center justify-between gap-2">
						<div class="text-[11px] text-blue-700">{{ item.label }}</div>
						<div class="text-sm font-semibold text-gray-900 text-right whitespace-nowrap leading-none">{{ item.value }}</div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="thresholdEntries.length" class="space-y-1">
			<div class="text-[11px] font-semibold uppercase tracking-wide text-blue-700">{{ thresholdSectionTitle }}</div>
			<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
				<div
					v-for="item in thresholdEntries"
					:key="item.key"
					:class="item.isCritical ? criticalCardClass : defaultCardClass"
				>
					<div class="w-full flex items-center justify-between gap-2">
						<div :class="item.isCritical ? criticalLabelClass : defaultLabelClass">{{ item.label }}</div>
						<div :class="item.isCritical ? criticalValueClass : defaultValueClass">{{ item.value }}</div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="distributionEntries.length" class="space-y-1">
			<div class="text-[11px] font-semibold uppercase tracking-wide text-blue-700">{{ distributionSectionTitle }}</div>
			<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
				<div
					v-for="item in distributionEntries"
					:key="item.key"
					:class="item.isCritical ? criticalCardClass : defaultCardClass"
				>
					<div class="w-full flex items-center justify-between gap-2">
						<div :class="item.isCritical ? criticalLabelClass : defaultLabelClass">{{ item.label }}</div>
						<div :class="item.isCritical ? criticalValueClass : defaultValueClass">{{ item.value }}</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type RuleValue = number | string | null | undefined;
type RuleRecord = Record<string, RuleValue>;

type AssessmentRules = {
	scoring?: RuleRecord;
	thresholds?: RuleRecord;
	Thresholds?: RuleRecord;
	criticalRule?: number | string;
	distribution?: RuleRecord;
	Distribution?: RuleRecord;
	questionDistribution?: RuleRecord;
	question_distribution?: RuleRecord;
	'Question Distribution'?: RuleRecord;
};

type ResolvedRuleSection = {
	title: string;
	values: Record<string, unknown>;
};

type RuleEntry = {
	key: string;
	label: string;
	value: string;
	isCritical: boolean;
};

const props = defineProps<{
	assessmentRules?: AssessmentRules | null;
}>();

const defaultCardClass = 'rounded bg-white/90 border border-blue-100 p-2 h-full flex items-center';
const criticalCardClass = 'rounded bg-red-50 border border-red-200 p-2 sm:col-span-2 lg:col-span-1 h-full flex items-center';
const defaultLabelClass = 'text-[11px] text-blue-700';
const criticalLabelClass = 'text-[11px] text-red-700 font-semibold';
const defaultValueClass = 'text-sm font-semibold text-gray-900 text-right whitespace-nowrap leading-none';
const criticalValueClass = 'text-sm font-semibold text-red-800 text-right whitespace-nowrap leading-none';

const normalizeRuleKey = (value: unknown): string =>
	String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '');

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const humanizeRuleLabel = (value: unknown): string => {
	const text = String(value ?? '')
		.trim()
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

	if (!text) {
		return '';
	}

	return text
		.split(' ')
		.map((part) => (part.length > 1 && part === part.toUpperCase() ? part : part.charAt(0).toUpperCase() + part.slice(1)))
		.join(' ');
};

const formatValue = (value: unknown): string => {
	if (value === null || value === undefined) {
		return 'N/A';
	}

	const text = String(value).trim();
	return text || 'N/A';
};

const formatPercentValue = (value: unknown): string => {
	const text = formatValue(value);
	if (text === 'N/A' || text.endsWith('%')) {
		return text;
	}

	return `${text}%`;
};

const isCriticalRule = (label: string): boolean => normalizeRuleKey(label).includes('critical');

const getRuleSection = (aliases: string[], fallbackTitle: string): ResolvedRuleSection => {
	const rules = props.assessmentRules;
	if (!isObjectRecord(rules)) {
		return { title: fallbackTitle, values: {} };
	}

	for (const alias of aliases) {
		const section = (rules as Record<string, unknown>)[alias];
		if (isObjectRecord(section)) {
			return { title: humanizeRuleLabel(alias) || fallbackTitle, values: section };
		}
	}

	const normalizedAliases = new Set(aliases.map((alias) => normalizeRuleKey(alias)));
	for (const [key, value] of Object.entries(rules)) {
		if (normalizedAliases.has(normalizeRuleKey(key)) && isObjectRecord(value)) {
			return { title: humanizeRuleLabel(key) || fallbackTitle, values: value };
		}
	}

	return { title: fallbackTitle, values: {} };
};

const buildRuleEntries = (
	values: Record<string, unknown>,
	options: { showPercent?: boolean; percentKeys?: string[] } = {}
): RuleEntry[] => {
	const percentKeys = new Set((options.percentKeys ?? []).map((key) => normalizeRuleKey(key)));

	return Object.entries(values)
		.filter(([key]) => String(key).trim().length > 0)
		.map(([key, rawValue]) => {
			const shouldShowPercent = Boolean(options.showPercent) || percentKeys.has(normalizeRuleKey(key));
			const label = humanizeRuleLabel(key);

			return {
				key,
				label,
				value: shouldShowPercent ? formatPercentValue(rawValue) : formatValue(rawValue),
				isCritical: isCriticalRule(label)
			};
		});
};

const sortCriticalEntriesLast = (entries: RuleEntry[]): RuleEntry[] =>
	[...entries].sort((a, b) => Number(a.isCritical) - Number(b.isCritical));

const scoringSection = computed(() => getRuleSection(['scoring', 'Scoring'], 'Scoring'));
const thresholdSection = computed(() => getRuleSection(['Thresholds', 'thresholds'], 'Thresholds'));
const distributionSection = computed(() =>
	getRuleSection(['Question Distribution', 'questionDistribution', 'question_distribution', 'distribution', 'Distribution'], 'Question Distribution')
);
const assessmentCriticalRule = computed(() => formatValue(props.assessmentRules?.criticalRule));

const scoringSectionTitle = computed(() => scoringSection.value.title);
const thresholdSectionTitle = computed(() => thresholdSection.value.title);
const distributionSectionTitle = computed(() => distributionSection.value.title);

const scoringEntries = computed(() =>
	buildRuleEntries(scoringSection.value.values, {
		percentKeys: ['passingScorePercent', 'passing_percent', 'overallPassingScore']
	})
);

const thresholdEntries = computed(() => {
	const entries = buildRuleEntries(thresholdSection.value.values, { showPercent: true });
	const hasCriticalThreshold = entries.some((entry) => entry.isCritical);

	if (!hasCriticalThreshold && assessmentCriticalRule.value !== 'N/A') {
		entries.push({
			key: 'criticalRule',
			label: 'Critical Items Passing Threshold',
			value: formatPercentValue(assessmentCriticalRule.value),
			isCritical: true
		});
	}

	return sortCriticalEntriesLast(entries);
});

const distributionEntries = computed(() => sortCriticalEntriesLast(buildRuleEntries(distributionSection.value.values)));

const hasAssessmentRules = computed(() =>
	Boolean(scoringEntries.value.length || thresholdEntries.value.length || distributionEntries.value.length)
);
</script>
